import { formatUomName } from "@/lib/uom";
import type { DecimalPrecision } from "@/utils/decimal";
import type { Quotation } from "@/types/salesDocuments";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getImageDataUrl,
  getPrintStyles,
  printHtml,
} from "./printHelpers";

interface QuotationPrintData {
  /** Company decimal precision. */
  precision: DecimalPrecision | number;
  quotation: Quotation;
  companyInfo: {
    businessName: string;
    addressLine0: string;
    addressLine1?: string;
    addressLine2?: string;
    cityName: string;
    postalZone: string;
    state: string;
    country: string;
    contactNumber: string;
    email: string;
    tin: string;
    registrationNumber: string;
    companyLogo?: string;
    reportFooter?: string;
  };
}

const getStatusClass = (status: string): string => {
  const statusMap: Record<string, string> = {
    Draft: "status-draft",
    Sent: "status-sent",
    Accepted: "status-confirmed",
    Rejected: "status-cancelled",
    Expired: "status-cancelled",
    Converted: "status-delivered",
  };
  return statusMap[status] || "status-draft";
};

const getStatusLabel = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const generateQuotationHtml = async (
  data: QuotationPrintData,
): Promise<string> => {
  const { quotation, companyInfo, precision } = data;

  // Get company logo if available
  let logoDataUrl = "";
  if (companyInfo.companyLogo) {
    try {
      logoDataUrl = await getImageDataUrl(
        `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/${companyInfo.companyLogo}`,
      );
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  }

  // Build company address
  const companyAddress = [
    companyInfo.addressLine0,
    companyInfo.addressLine1,
    companyInfo.addressLine2,
    companyInfo.postalZone,
    companyInfo.cityName,
    companyInfo.state,
    companyInfo.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Build buyer address
  const buyerAddress = [
    quotation.buyerAddressLine0,
    quotation.buyerAddressLine1,
    quotation.buyerAddressLine2,
    quotation.buyerPostcode,
    quotation.buyerCity,
    quotation.buyerState,
    quotation.buyerCountry,
  ]
    .filter(Boolean)
    .join(", ");

  // Calculate totals from actual line items
  const computedTotalExcludingTax = quotation.lineItems.reduce(
    (sum, item) => sum + Number(item.totalExcludingTax || 0),
    0,
  );
  const computedTotalTaxAmount = quotation.lineItems.reduce(
    (sum, item) => sum + Number(item.taxAmount || 0),
    0,
  );
  const computedTotalIncludingTax =
    computedTotalExcludingTax + computedTotalTaxAmount;
  const additionalDiscount = Number(
    quotation.additionalDiscountAmount || 0,
  );
  const additionalFee = Number(quotation.additionalFeeAmount || 0);
  const rounding = Number(quotation.roundingAmount || 0);
  const computedTotalPayable =
    computedTotalIncludingTax - additionalDiscount + additionalFee + rounding;

  // Generate line items HTML
  const lineItemsHtml = quotation.lineItems
    .map(
      (item, index) => `
    <tr>
      <td class="text-center">${index + 1}</td>
      <td>${item.itemCode || ""}</td>
      <td>
        <strong>${item.description}</strong>
        ${item.classification ? `<br/><small>Class: ${item.classification}</small>` : ""}
      </td>
      <td class="text-center">${item.quantity}</td>
      <td class="text-center">${formatUomName(item.measurement) || "Unit"}</td>
      <td class="text-right">${formatCurrency(item.unitPrice, quotation.currencyCode, precision)}</td>
      <td class="text-right">${(item.discountRate ?? 0) > 0 ? item.discountRate + "%" : "-"}</td>
      <td class="text-right">${item.taxRate > 0 ? item.taxRate + "%" : "-"}</td>
      <td class="text-right">${formatCurrency(item.taxAmount, quotation.currencyCode, precision)}</td>
      <td class="text-right"><strong>${formatCurrency(item.total, quotation.currencyCode, precision)}</strong></td>
    </tr>
  `,
    )
    .join("");

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Quotation ${quotation.codeNumber}</title>
      ${getPrintStyles()}
    </head>
    <body>
      <div class="container">
        <!-- Header -->
        <div class="header">
          <div class="company-info">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="company-logo"/>` : ""}
            <div class="company-name">${companyInfo.businessName}</div>
            <div class="company-details">
              <div>${companyAddress}</div>
              <div>Tel: ${companyInfo.contactNumber} | Email: ${companyInfo.email}</div>
              <div>TIN: ${companyInfo.tin} | Reg No: ${companyInfo.registrationNumber}</div>
            </div>
          </div>
          <div class="document-title">
            <div class="document-type">QUOTATION</div>
            <div class="document-number">${quotation.codeNumber}</div>
            <div class="document-date">Date: ${formatDate(quotation.quotationDate)}</div>
            ${quotation.validUntil ? `<div class="document-date">Valid Until: ${formatDate(quotation.validUntil)}</div>` : ""}
            <div class="status-badge ${getStatusClass(quotation.status)}">
              ${getStatusLabel(quotation.status)}
            </div>
          </div>
        </div>

        <!-- Buyer Information -->
        <div class="parties-section">
          <div class="party-box">
            <div class="party-title">QUOTATION FOR</div>
            <div class="party-details">
              <div><strong>Name:</strong> ${quotation.buyerName}</div>
              ${quotation.buyerTin ? `<div><strong>TIN:</strong> ${quotation.buyerTin}</div>` : ""}
              ${quotation.buyerRegistrationNumber ? `<div><strong>Reg No:</strong> ${quotation.buyerRegistrationNumber}</div>` : ""}
              <div><strong>Address:</strong> ${buyerAddress}</div>
              ${quotation.buyerContact ? `<div><strong>Tel:</strong> ${quotation.buyerContact}</div>` : ""}
              ${quotation.buyerEmail ? `<div><strong>Email:</strong> ${quotation.buyerEmail}</div>` : ""}
            </div>
          </div>
        </div>

        <!-- Line Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th style="width: 80px;">Item Code</th>
              <th>Description</th>
              <th style="width: 50px;">Qty</th>
              <th style="width: 50px;">UOM</th>
              <th style="width: 80px;">Unit Price</th>
              <th style="width: 60px;">Disc %</th>
              <th style="width: 60px;">Tax %</th>
              <th style="width: 80px;">Tax Amt</th>
              <th style="width: 90px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHtml}
          </tbody>
        </table>

        <!-- Totals Section -->
        <div class="totals-section">
          <table class="totals-table">
            <tr>
              <td>Subtotal (Excl. Tax):</td>
              <td>${formatCurrency(computedTotalExcludingTax, quotation.currencyCode, precision)}</td>
            </tr>
            <tr>
              <td>Total Tax Amount:</td>
              <td>${formatCurrency(computedTotalTaxAmount, quotation.currencyCode, precision)}</td>
            </tr>
            <tr>
              <td>Total (Incl. Tax):</td>
              <td>${formatCurrency(computedTotalIncludingTax, quotation.currencyCode, precision)}</td>
            </tr>
            <tr class="total-row">
              <td>TOTAL PAYABLE:</td>
              <td>${formatCurrency(computedTotalPayable, quotation.currencyCode, precision)}</td>
            </tr>
          </table>
        </div>

        <!-- Terms and Conditions -->
        ${
          quotation.termsAndConditions
            ? `
        <div class="footer-section">
          <div class="footer-title">Terms and Conditions</div>
          <div class="footer-content">${quotation.termsAndConditions}</div>
        </div>
        `
            : ""
        }

        <!-- Remarks -->
        ${
          quotation.remarks
            ? `
        <div class="footer-section">
          <div class="footer-title">Remarks</div>
          <div class="footer-content">${quotation.remarks}</div>
        </div>
        `
            : ""
        }

        <!-- Report Footer -->
        ${
          companyInfo.reportFooter
            ? `
        <div class="footer-section">
          <div class="footer-content">${companyInfo.reportFooter}</div>
        </div>
        `
            : ""
        }

        <!-- Footer Note -->
        <div class="footer-section" style="margin-top: 30px; font-size: 8pt; color: #6b7280; text-align: center;">
          <p>This is a computer-generated document. No signature is required.</p>
          <p>Generated on ${formatDateTime(new Date())}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};

export const printQuotation = async (data: QuotationPrintData) => {
  const html = await generateQuotationHtml(data);
  printHtml(html, `Quotation_${data.quotation.codeNumber}`);
};
